import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ListChecks, RotateCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, ctLabel } from "./dashboard/primitives";
import { KpiCards } from "./dashboard/KpiCards";
import { HelpTip } from "@/components/help/helpShared";
import { PerformanceChart } from "./dashboard/PerformanceChart";
import { GoalHero } from "./dashboard/GoalHero";
import { GoalFunnel } from "./dashboard/GoalFunnel";
import { NeedsAttention, TodayUpcoming } from "./dashboard/AttentionToday";
import { PipelineFunnel, ConversionFunnel } from "./dashboard/PipelineVisuals";
import { ProgramProgress, SourceChart } from "./dashboard/ProgramSource";
import { PipelineHealth, RecentActivity, RecruiterWorkload } from "./dashboard/HealthActivityWorkload";

const RANGES = [["this_week", "This Week"], ["this_month", "This Month"], ["qtd", "Quarter to Date"], ["this_year", "Year to Date"], ["last_12", "Last 12 Months"], ["all", "All Time"]];
// Explicit, role-aware scope labels. District/Account resolve to team-scope on the backend (which is RBAC-capped
// to the user's authorized account/district); the selector NEVER expands access.
const SCOPE_LABELS = { mine: "My Results", team: "My Team", district: "District", account: "Account" };
const apiView = (v) => (v === "mine" ? "mine" : "team");

export default function RecruitingDashboard({ onDrill, personal = false }) {
  const { user, isManager, isAdmin, isSuper, isGroupAdmin } = useAuth();
  const navigate = useNavigate();
  const canTeam = (isManager || isAdmin || isSuper || isGroupAdmin) && !personal;
  const canManageGoals = isManager || isAdmin || isSuper || isGroupAdmin;
  const [range, setRange] = useState(() => localStorage.getItem("tr_dash_range") || "this_year");
  const [dview, setDview] = useState(() => {
    if (personal) return "mine";
    let v = localStorage.getItem("tr_dash_view");
    if (v === "all") v = "team";   // migrate legacy vague "All" value
    return v || ((isManager || isAdmin || isSuper || isGroupAdmin) ? "team" : "mine");
  });
  const [pipeline, setPipeline] = useState("all");
  const [pipelines, setPipelines] = useState([]);
  const [data, setData] = useState(null);
  const [dateKpis, setDateKpis] = useState(null);
  const [goals, setGoals] = useState(null);
  const [today, setToday] = useState(null);
  const [top10, setTop10] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reassign, setReassign] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => { api.get("/pipelines").then((r) => setPipelines((r.data.items || []).filter((p) => p.active !== false))).catch(() => {}); }, []);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.users || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true); setErr(false);
    const district_org_id = localStorage.getItem("tr_district") || "";
    api.get("/recruiting/dashboard", { params: { view: apiView(dview), range, pipeline, district_org_id } })
      .then((r) => setData(r.data)).catch((e) => { setErr(true); toast.error(apiError(e)); }).finally(() => setLoading(false));
    api.get("/recruiting/metrics", { params: { view: apiView(dview), range, district_org_id } })
      .then((r) => setDateKpis(r.data)).catch(() => setDateKpis({ counts: {} }));
    api.get("/recruiting/goals/progress", { params: { year: new Date().getFullYear(), view: apiView(dview), district_org_id } })
      .then((r) => setGoals(r.data)).catch(() => setGoals({ has_goal: false }));
    const start = new Date().toISOString().slice(0, 10);
    const end = new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
    api.get("/calendar/my", { params: { start, end } }).then((r) => setToday(r.data)).catch(() => setToday({ items: [] }));
    api.get("/tasks/top10").then((r) => setTop10(r.data)).catch(() => setTop10({ items: [], suggestions: [] }));
  }, [dview, range, pipeline]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("tr-district-change", h);
    return () => window.removeEventListener("tr-district-change", h);
  }, [load]);

  const setRangeP = (v) => { setRange(v); localStorage.setItem("tr_dash_range", v); };
  const setViewP = (v) => { setDview(v); localStorage.setItem("tr_dash_view", v); };

  const drill = (params) => onDrill?.(params);
  const openKpiDrill = (metric) => navigate(`/reports/metric-list?${new URLSearchParams({ metric, range, view: apiView(dview),
    district_org_id: localStorage.getItem("tr_district") || "" })}`);
  const openOpp = (id) => navigate(`/recruiting/opportunity/${id}`);
  const snooze = async (oppId, days) => {
    try { await api.post(`/recruiting/opportunities/${oppId}/snooze`, { days }); toast.success(days ? `Snoozed ${days} days` : "Unsnoozed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openReassign = async () => {
    try { const { data } = await api.get("/opportunities", { params: { scope: "unassigned" } }); setReassign({ opps: data.opportunities || [] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (err && !data) return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center" data-testid="dashboard-error">
      <p className="text-rose-700 font-medium mb-3">We couldn't load the dashboard.</p>
      <Button variant="outline" onClick={load} data-testid="dashboard-retry"><RotateCw className="w-4 h-4 mr-1.5" />Retry</Button>
    </div>
  );

  const refreshing = loading && !!data;
  const viewLabel = SCOPE_LABELS[dview] || "My Results";
  const districtLabel = goals?.scope_district_name || (localStorage.getItem("tr_district") ? "" : "All My Districts");
  const contextLabel = [viewLabel, districtLabel].filter(Boolean).join(" · ");

  return (
    <MotionConfig reducedMotion="user">
      <div className="space-y-4" data-testid="recruiting-dashboard">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={range} onValueChange={setRangeP}>
            <SelectTrigger className="h-9 w-40" data-testid="dash-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RANGES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <HelpTip label="dash-period" text="The reporting period. All KPI cards and the performance chart use this period, and the card labels update to match (e.g. Leads YTD)." slug="recruiting-dashboard-kpis" />
          {canTeam && (
            <Select value={dview} onValueChange={setViewP}>
              <SelectTrigger className="h-9 w-40" data-testid="dash-view"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="mine">My Results</SelectItem>
                <SelectItem value="team">My Team</SelectItem>
                {(isGroupAdmin || isAdmin) && <SelectItem value="district">District</SelectItem>}
                {(isAdmin || isSuper) && <SelectItem value="account">Account</SelectItem>}
              </SelectContent>
            </Select>
          )}
          {canTeam && <HelpTip label="dash-scope" text="Scope of the results. My Results shows records assigned to you; My Team, District and Account widen the view — you only see scopes your role allows." slug="dashboard-scopes" />}
          {refreshing && <span className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="dash-refreshing"><RotateCw className="w-3.5 h-3.5 animate-spin" />Updating…</span>}
          {contextLabel && <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-3 py-1" data-testid="dash-context">{contextLabel}</span>}
        </div>

        <div className={`space-y-4 transition-opacity duration-200 ${refreshing ? "opacity-60" : "opacity-100"}`}>
          <KpiCards metrics={dateKpis} range={range} loading={loading} onKpiDrill={openKpiDrill} />

          <PerformanceChart data={data} loading={loading} index={1} interviewMonths={dateKpis?.interview_months} />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <NeedsAttention data={data} loading={loading} drill={drill} openOpp={openOpp} onSnooze={snooze}
              onReassignOne={(r) => setReassign({ opps: [{ id: r.opportunity_id, person_name: r.name, candidate_type: r.candidate_type }] })} index={2} />
            <TodayUpcoming today={today} navigate={navigate} index={3} />
          </div>

          <PipelineFunnel data={data} loading={loading} pipeline={pipeline} setPipeline={setPipeline} pipelines={pipelines} drill={drill} index={4} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ConversionFunnel data={data} loading={loading} drill={drill} index={5} />
            <GoalFunnel progress={goals} drill={drill} index={6} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <ProgramProgress data={data} loading={loading} drill={drill} index={7} />
            <Panel title="My Daily Top 10" icon={ListChecks} testId="daily-top10" className="lg:col-span-5" index={8}
              action={<button className="text-xs text-brand font-medium" data-testid="top10-view" onClick={() => navigate("/tasks")}>Tasks</button>}>
              {!top10 ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : <Top10 data={top10} onChange={() => api.get("/tasks/top10").then((r) => setTop10(r.data))} navigate={navigate} />}
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <PipelineHealth data={data} loading={loading} drill={drill} index={9} />
            <RecentActivity data={data} loading={loading} navigate={navigate} index={10} />
            <SourceChart data={data} loading={loading} drill={drill} index={11} />
          </div>

          {!personal && <RecruiterWorkload data={data} loading={loading} drill={drill} openReassign={openReassign} contextLabel={contextLabel} index={12} />}

          {goals ? <GoalHero progress={goals} navigate={navigate} canEdit={goals?.can_edit} canManage={canManageGoals} onManage={() => navigate("/admin/recruiting-goals")} index={13} /> : <Skeleton h="h-52" />}
        </div>

        {reassign && <ReassignDialog opps={reassign.opps} users={users} currentUserId={user?.id} onClose={() => setReassign(null)} onDone={() => { setReassign(null); load(); }} />}
      </div>
    </MotionConfig>
  );
}

function ReassignDialog({ opps, users, currentUserId, onClose, onDone }) {
  const [list, setList] = useState(opps);
  const assign = async (opp, recruiterId) => {
    try {
      await api.patch(`/opportunities/${opp.id}`, { assigned_recruiter_id: recruiterId });
      toast.success("Recruiter assigned");
      setList((l) => l.filter((o) => o.id !== opp.id));
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && (list.length !== opps.length ? onDone() : onClose())}>
      <DialogContent className="bg-white max-w-lg" data-testid="reassign-dialog">
        <DialogHeader><DialogTitle>Assign Unassigned Candidates</DialogTitle></DialogHeader>
        {list.length ? (
          <div className="space-y-2 max-h-[55vh] overflow-auto">
            {list.map((o) => (
              <div key={o.id} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5" data-testid={`reassign-${o.id}`}>
                <UserAvatar name={o.person_name} size={30} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{o.person_name}</p>
                  <p className="text-[11px] text-slate-400 truncate">{ctLabel(o.candidate_type)}{o.organization_name ? ` · ${o.organization_name}` : ""}</p>
                </div>
                <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => assign(o, currentUserId)} data-testid={`reassign-me-${o.id}`}>Assign to me</Button>
                <select className="h-8 rounded-md border border-slate-200 text-xs px-1.5 max-w-[130px]" defaultValue="" onChange={(e) => e.target.value && assign(o, e.target.value)} data-testid={`reassign-select-${o.id}`}>
                  <option value="" disabled>Pick recruiter…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        ) : <EmptyState icon={CheckCircle2} title="All assigned" description="No unassigned candidates left." />}
        <DialogFooter><Button variant="outline" onClick={() => (list.length !== opps.length ? onDone() : onClose())} data-testid="reassign-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Top10({ data, onChange, navigate }) {
  const items = data.items || [];
  const suggestions = data.suggestions || [];
  const complete = async (t) => { try { await api.patch(`/tasks/${t.id}`, { status: "completed" }); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (t) => { try { await api.post(`/tasks/${t.id}/top10`, { on: false }); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const add = async (t) => { try { await api.post(`/tasks/${t.id}/top10`, { on: true }); onChange(); } catch (e) { toast.error(apiError(e)); } };
  if (!items.length && !suggestions.length) return <EmptyState icon={ListChecks} title="Your Top 10 is empty" description="Add priorities to focus your day." />;
  return (
    <div className="space-y-1.5">
      {items.map((t) => (
        <div key={t.id} className="flex items-center gap-2 group" data-testid={`top10-${t.id}`}>
          <input type="checkbox" onChange={() => complete(t)} className="rounded border-slate-300" data-testid={`top10-check-${t.id}`} />
          <button onClick={() => navigate(`/tasks/detail/${t.id}`)} className="text-sm text-slate-700 hover:text-brand truncate flex-1 text-left">{t.title}</button>
          {t.importance && <span className="text-[10px] text-slate-400 capitalize">{t.importance}</span>}
          <button onClick={() => remove(t)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 text-xs" data-testid={`top10-remove-${t.id}`}>✕</button>
        </div>
      ))}
      {suggestions.length > 0 && (
        <div className="pt-2 mt-1 border-t border-slate-100">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Suggested</p>
          {suggestions.slice(0, 3).map((t) => (
            <div key={t.id} className="flex items-center gap-2" data-testid={`top10-sugg-${t.id}`}>
              <span className="text-sm text-slate-500 truncate flex-1">{t.title}</span>
              <button onClick={() => add(t)} className="text-xs text-brand font-medium" data-testid={`top10-add-${t.id}`}>+ Add</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
